'use client';
import Fade from '@mui/material/Fade';
import MuiTooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import withRef from '../../utils/withRef';
import Typography from '../Typography';
import { styles } from './styles';

const useStyles = makeStyles()(styles);

export const Tooltip = ({
  classes: classesProp = {},
  body,
  title,
  subtitle,
  extraLabels = [],
  variant = 'dark',
  placement,
  peek,
  children,
  PopperProps,
  slots,
  slotProps,
  forwardedRef: ref,
  enterDelay = 500,
  leaveDelay = 200,
  enterNextDelay = 500,
  ...rest
}) => {
  const { open: openProp, onOpen: onOpenProp, onClose: onCloseProp, ...restForMui } = rest;
  const { classes, cx } = useStyles();
  const [childMenuOpen, setChildMenuOpen] = React.useState(false);
  /** Hover/focus open state when Tooltip is not controlled by the parent (`open` prop). */
  const [fallbackOpen, setFallbackOpen] = React.useState(false);
  /**
   * After the menu closes, MUI may still run a hover enterTimer scheduled before the menu opened.
   * Ignore `onOpen` until this timestamp so the tooltip does not pop in until the user moves the pointer.
   */
  const suppressFallbackOpenUntilRef = React.useRef(0);
  const hasContent = title || title === 0 || body || subtitle;
  const size = peek || body || subtitle || extraLabels.length ? 'large' : 'small';
  const isBulletedList = Array.isArray(body);
  const bodyContent = body && !isBulletedList ? body : subtitle;
  const classKey = `${size}-${variant}`;

  // Only DropMenu-based controls (`menuItems`) handle `onOpenChange`. Other composites (e.g. MUI
  // `Box`) would forward unknown props to the DOM and trigger React warnings.
  const childCanReceiveMenuProps =
    React.isValidElement(children) &&
    typeof children.type !== 'string' &&
    typeof children.type !== 'symbol' &&
    Array.isArray(children.props?.menuItems);

  const handleDropMenuOpenChange = (open) => {
    setChildMenuOpen(open);
    if (!open) {
      setFallbackOpen(false);
      suppressFallbackOpenUntilRef.current =
        Date.now() + Math.max(enterDelay, enterNextDelay) + 100;
    }
    if (childCanReceiveMenuProps && typeof children.props.onOpenChange === 'function') {
      children.props.onOpenChange(open);
    }
  };

  const childWithMenuNotifier =
    childCanReceiveMenuProps && React.isValidElement(children)
      ? React.cloneElement(children, { onOpenChange: handleDropMenuOpenChange })
      : children;

  React.useEffect(() => {
    if (childMenuOpen) {
      setFallbackOpen(false);
    }
  }, [childMenuOpen]);

  /**
   * MUI Tooltip locks controlled vs uncontrolled on first mount. Passing `open` only after the menu
   * opens therefore does not hide the tooltip. When the child can report menu open (DropMenu), we
   * stay controlled from the first render using `fallbackOpen` + `onOpen` / `onClose`.
   */
  const menuAwareOpen = childMenuOpen ? false : openProp !== undefined ? openProp : fallbackOpen;

  const menuAwareOpenProps = childCanReceiveMenuProps
    ? {
        open: menuAwareOpen,
        onOpen: (event) => {
          if (childMenuOpen) {
            return;
          }
          if (Date.now() < suppressFallbackOpenUntilRef.current) {
            return;
          }
          if (openProp === undefined) {
            setFallbackOpen(true);
          }
          onOpenProp?.(event);
        },
        onClose: (event) => {
          if (openProp === undefined) {
            setFallbackOpen(false);
          }
          onCloseProp?.(event);
        },
      }
    : {
        ...(openProp !== undefined ? { open: openProp } : {}),
        ...(onOpenProp ? { onOpen: onOpenProp } : {}),
        ...(onCloseProp ? { onClose: onCloseProp } : {}),
      };

  return hasContent || extraLabels.length ? (
    <MuiTooltip
      arrow
      classes={{
        ...classesProp,
        tooltip: cx(
          classes[`${classKey}-tooltip`],
          classes[`${size}-${placement}`],
          extraLabels.length || body || (title && subtitle)
            ? classes.tooltipLarge
            : classes.tooltipSmall,
          peek && classes.peek,
          classesProp.tooltip
        ),
      }}
      title={
        <>
          <div className={classes[`${classKey}-title`]}>{title}</div>
          <div className={classes[`${classKey}-subtitle`]}>{bodyContent}</div>
          {extraLabels?.map(({ title: extraTitle, subtitle }, i) => (
            <div className={cx((title || i !== 0) && classes[`${classKey}-labelSpacer`])} key={i}>
              <div className={classes[`${classKey}-title`]}>{extraTitle}</div>
              <div className={classes[`${classKey}-subtitle`]}>{subtitle}</div>
            </div>
          ))}
          {isBulletedList &&
            body.map((listItem, i) => (
              <Typography variant="bodySmall" key={i} className={classes[`${classKey}-subtitle`]}>
                {`• ${listItem}`}
              </Typography>
            ))}
          <span className={classes.arrow} />
        </>
      }
      placement={placement}
      slots={{
        transition: Fade,
        ...slots,
      }}
      slotProps={{
        ...slotProps,
        popper: {
          ...PopperProps,
          ...slotProps?.popper,
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                mainAxis: false,
              },
            },
            ...(PopperProps?.modifiers || []),
            ...(slotProps?.popper?.modifiers || []),
          ],
        },
        transition: {
          ...slotProps?.transition,
          style: {
            transition:
              'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            ...slotProps?.transition?.style,
          },
        },
      }}
      enterDelay={peek ? 0 : enterDelay}
      leaveDelay={peek ? 0 : leaveDelay}
      enterNextDelay={peek ? 0 : enterNextDelay}
      {...restForMui}
      {...menuAwareOpenProps}
      ref={ref}
    >
      {childWithMenuNotifier}
    </MuiTooltip>
  ) : (
    children
  );
};

Tooltip.propTypes = {
  /** Content used for body of `Tooltip`. Bulleted list. */
  body: PropTypes.node,
  /** Element that triggers tooltip on hover. */
  children: PropTypes.element,
  /** Override or extend the styles applied to the component. */
  classes: PropTypes.object,
  /** If `true`, the tooltip will not be interactive, i.e. it will close when the user hovers over the tooltip. */
  disableInteractive: PropTypes.bool,
  /** The number of milliseconds to wait before showing the tooltip. */
  enterDelay: PropTypes.number,
  /** The number of milliseconds to wait before showing the tooltip when one was already recently opened. */
  enterNextDelay: PropTypes.number,
  /** Additional text items to be displayed in tooltip. */
  extraLabels: PropTypes.arrayOf(
    PropTypes.shape({
      /** Text for an additional tooltip subtitle. */
      subtitle: PropTypes.node.isRequired,
      /** Text for an additional tooltip title. */
      title: PropTypes.node.isRequired,
    })
  ),
  /** The number of milliseconds to wait before hiding the tooltip. */
  leaveDelay: PropTypes.number,
  /** Placement of the tooltip in relation to the anchor el. */
  placement: PropTypes.oneOf([
    'left',
    'top',
    'bottom',
    'right',
    'left-end',
    'top-end',
    'bottom-end',
    'right-end',
    'left-start',
    'top-start',
    'bottom-start',
    'right-start',
  ]),
  /** The props used for each slot inside. */
  slotProps: PropTypes.shape({
    /** Props forwarded to the arrow slot. */
    arrow: PropTypes.object,
    /** Props forwarded to the popper slot. */
    popper: PropTypes.object,
    /** Props forwarded to the tooltip slot. */
    tooltip: PropTypes.object,
    /** Props forwarded to the transition slot. */
    transition: PropTypes.object,
  }),
  /** The components used for each slot inside. */
  slots: PropTypes.shape({
    /** The component used for the arrow. */
    arrow: PropTypes.elementType,
    /** The component used for the popper. */
    popper: PropTypes.elementType,
    /** The component used for the tooltip. */
    tooltip: PropTypes.elementType,
    /** The component used for the transition. */
    transition: PropTypes.elementType,
  }),
  /** Text for tooltip subtitle. */
  subtitle: PropTypes.node,
  /** Text for tooltip title. */
  title: PropTypes.node,
  /** The variant to use. */
  variant: PropTypes.oneOf(['dark', 'light']),
};

export default withRef()(Tooltip);
